import { createStore, delMany, getMany, keys as idbKeys, set, type UseStore } from 'idb-keyval'
import type { WeatherReport } from '../../shared/contract'

/**
 * The last complete report per saved place, kept in IndexedDB (idb-keyval) so the app can
 * paint the moment it opens — like Apple Weather — while the engine refreshes in the
 * background. Bounded to the saved places (+ My Location); every access is best-effort:
 * private mode, blocked storage or quota errors just mean no instant paint.
 */

/**
 * Reports older than this are never painted on open (they'd pass for current conditions);
 * the place shows its loading state until the engine answers instead.
 */
export const INSTANT_MAX_AGE_MS = 6 * 3_600_000
/** With no network, older reports are still shown — labelled "Offline — showing data from …". */
export const OFFLINE_MAX_AGE_MS = 48 * 3_600_000

declare global { interface Window { __wfSavedReports?: Promise<[string, unknown][]> } }

interface Saved {
  v: 1
  savedAt: number
  report: WeatherReport
}

let store: UseStore | null | undefined

function db(): UseStore | null {
  if (store === undefined) {
    try {
      store = typeof indexedDB === 'undefined' ? null : createStore('weather-fusion-app', 'last-reports')
    } catch {
      store = null
    }
  }
  return store
}

export const reportKey = (latitude: number, longitude: number) => `${latitude.toFixed(3)},${longitude.toFixed(3)}`

export const reportAge = (r: WeatherReport, now = Date.now()) => now - Date.parse(r.generated_at)

const isSaved = (x: unknown): x is Saved =>
  !!x && typeof x === 'object' && (x as Saved).v === 1 && typeof (x as Saved).report?.generated_at === 'string'

/** Tiny boot hint only: values still come from validated IndexedDB reports. */
function rememberReport(key: string, report: WeatherReport) {
  try {
    const hints = JSON.parse(localStorage.getItem('wf2-report-hints') ?? '{}') as Record<string, number>
    hints[key] = Date.parse(report.generated_at)
    const recent = Object.entries(hints).filter(([, at]) => Number.isFinite(at) && Date.now() - at <= OFFLINE_MAX_AGE_MS)
      .sort((a, b) => b[1] - a[1]).slice(0, 24)
    localStorage.setItem('wf2-report-hints', JSON.stringify(Object.fromEntries(recent)))
  } catch { /* storage is optional */ }
}

/** Saved reports for these keys (missing, malformed or too old entries are left out). */
export async function readReports(keys: string[], maxAgeMs = INSTANT_MAX_AGE_MS): Promise<Map<string, WeatherReport>> {
  const out = new Map<string, WeatherReport>()
  if (!keys.length) return out
  try {
    const early = typeof window === 'undefined' ? undefined : await window.__wfSavedReports
    const ready = new Map(early ?? [])
    const missing = keys.filter((key) => !ready.has(key))
    if (missing.length) {
      const s = db()
      if (s) {
        const fetched = await getMany(missing, s)
        missing.forEach((key, i) => ready.set(key, fetched[i]))
      }
    }
    const values = keys.map((key) => ready.get(key))
    values.forEach((v, i) => {
      if (isSaved(v) && reportAge(v.report) <= maxAgeMs) { out.set(keys[i], v.report); rememberReport(keys[i], v.report) }
    })
  } catch {
    /* storage unavailable */
  }
  return out
}

export async function saveReport(key: string, report: WeatherReport): Promise<void> {
  const s = db()
  if (!s) return
  // Only complete reports are worth instant-painting later.
  if (report.sources.some((src) => /^still loading/i.test(src.detail))) return
  try {
    await set(key, { v: 1, savedAt: Date.now(), report } satisfies Saved, s)
    rememberReport(key, report)
  } catch {
    /* quota or private mode */
  }
}

/** How many reports are saved for instant paint (Settings → Diagnostics). */
export async function countReports(): Promise<number> {
  const s = db()
  if (!s) return 0
  try {
    return (await idbKeys(s)).length
  } catch {
    return 0
  }
}

/** Forgets every saved report (Settings → Diagnostics → Clear cache). */
export async function clearReports(): Promise<void> {
  try { localStorage.removeItem('wf2-report-hints') } catch { /* storage is optional */ }
  const s = db()
  if (!s) return
  try {
    const all = await idbKeys(s)
    if (all.length) await delMany(all, s)
  } catch {
    /* ignore */
  }
}

/** Drops reports for places no longer saved. */
export async function pruneReports(keep: Set<string>): Promise<void> {
  const s = db()
  if (!s) return
  try {
    const gone = (await idbKeys(s)).map(String).filter((k) => !keep.has(k))
    if (gone.length) await delMany(gone, s)
  } catch {
    /* ignore */
  }
}
